import type { MatchRow } from '../../types';
import type { UndoRowB } from './bandB';
import { BoardTileB, EmptyBoardSlotB } from './BoardTileB';
import { HypothesisSheetB } from './HypothesisSheetB';
import { useLockedBoardB } from './useLockedBoardB';

/**
 * B-10 — the **locked board** capture surface (design spec §2.4; scenario 12): the middle band
 * of the takeover chrome while `capturePhase = 'locked'`. `BOARD_CAP` cells in a 2×4 grid whose
 * geometry is fixed — rows divide the band's height, a long hypothesis clamps its text, and
 * every state change (increment, promotion, override) is paint inside a fixed box. The only
 * thing that ever changes the *order* of tiles is INV-11's derived re-rank (disproved sinks
 * below active), applied through the quiet gate and never while a finger is down; `boardPosition`
 * itself is assigned once at lock and no code on this surface can write it (INV-4 by op-set type).
 *
 * ── What is deliberately absent, and whose it is ─────────────────────────────
 *
 * No board-change affordance exists here: `SwapBoardTile` is **halftime-only** by engine window
 * (F-3's accepted reading), so filling a slot or displacing a tile is the break hub's board
 * manage (**B-14's**), never a mid-play control. The undo pill is **B-11's** bottom band. The
 * "Discovery" link is navigation, not a phase change (`canLock` semantics — there is no
 * `locked → discovery` transition to dispatch). A long press opens B-15's hypothesis detail
 * sheet, non-modal over this band so the whistle and the pill stay live around it.
 */
export interface LockedBoardSurfaceBProps {
  /** The shell's fresh row — the single authority on state; re-read after every transition. */
  readonly match: MatchRow;
  /** Scenario 12/LOCK-5 — visit the discovery surface (navigation; absent on the pre-kickoff preview). */
  readonly onVisitDiscovery?: () => void;
  /** B-11 — the surface publishes its committed tile state for the band's undo pill. */
  readonly onRowsChanged?: (rows: readonly UndoRowB[]) => void;
  /** B-11 — bumped after a band undo commits; the surface re-reads and republishes. */
  readonly refreshEpoch?: number;
}

export function LockedBoardSurfaceB({
  match,
  onVisitDiscovery,
  onRowsChanged,
  refreshEpoch,
}: LockedBoardSurfaceBProps) {
  const { state, gestures, actions } = useLockedBoardB(match, { onRowsChanged, refreshEpoch });

  if (state.phase === 'loading') {
    return (
      <p className="capb-loading" data-testid="boardb-loading">
        Opening the board…
      </p>
    );
  }
  if (state.phase === 'failed') {
    return (
      <p className="capb-loading" role="alert" data-testid="boardb-failed">
        The board could not be loaded. The log has the detail.
      </p>
    );
  }

  return (
    <div className="boardb" data-testid="boardb" data-capturing={state.capturing ? 'true' : 'false'}>
      {/* §6.4a's negative made visible (the B-9 discipline): pre-kickoff taps record nothing,
          and the surface says so rather than swallowing them. */}
      {!state.capturing && (
        <p className="boardb-preview" data-testid="boardb-not-capturing">
          Not kicked off — taps record nothing yet. Hold the whistle above to start.
        </p>
      )}

      {/* INV-5 / NFR-8 — never a silent degradation: an illegal board still renders every tile
          it holds, and says so instead of quietly dropping one. */}
      {state.integrity !== null && (
        <p className="boardb-warn" role="alert" data-testid="boardb-integrity">
          {state.integrity}
        </p>
      )}

      {state.notice !== null && (
        <div className="discb-notice" role="status" data-testid="boardb-notice">
          <span>{state.notice}</span>
          <button type="button" onClick={actions.dismissNotice}>
            Dismiss
          </button>
        </div>
      )}

      {onVisitDiscovery !== undefined && (
        <div className="boardb-head">
          <span className="boardb-head__label">Locked board</span>
          <button
            type="button"
            className="boardb-visit"
            data-testid="boardb-to-discovery"
            onClick={onVisitDiscovery}
          >
            Discovery
          </button>
        </div>
      )}

      <div className="boardb-grid" data-testid="boardb-grid">
        {state.slots.map((slot, index) =>
          slot === null ? (
            // `index` is the only identity an empty cell has, and empty cells never reorder
            // among themselves: they are the tail padding of a packed render, so the usual
            // index-key hazard cannot arise — an empty cell holds no state at all.
            <EmptyBoardSlotB key={`empty-${String(index)}`} index={index} />
          ) : (
            // Keyed by hypothesis id, never by cell: tiles carry imperative rendered state
            // (`data-ack`, `data-pressed`) and this grid re-ranks by requirement (LIVE-3).
            // Positional reconciliation would hand a moving tile the DOM node — and the ack —
            // of the tile that used to sit there.
            <BoardTileB key={slot.id} tile={slot} gestures={gestures} capturing={state.capturing} />
          ),
        )}
      </div>

      {/* B-15's detail sheet, opened by the 450 ms hold on any tile (§2.4). Non-modal over the
          middle band: the board stays mounted behind it and nothing outside is inerted. */}
      {state.detailTile !== null && (
        <HypothesisSheetB
          match={match}
          hypothesis={state.detailTile}
          onChanged={actions.reread}
          onClose={actions.closeDetail}
        />
      )}
    </div>
  );
}
